import { useEffect, useRef, useState } from "react";
import AgentCardPopover from "./AgentCardPopover";
import DisplayTitleEditor from "./DisplayTitleEditor";
import FifaVerifiedBadge, {
  isFifaVerified,
} from "../common/FifaVerifiedBadge";
import {
  Card,
  CardGrid,
  CardHeader,
  CardName,
  StatChip,
  ContactRow,
  CopyToast,
  PersonAvatar,
  AdminPill,
  LawyerPill,
  OverflowButton,
  Button,
  PlusIcon,
  UsersPlusIcon,
  CheckIcon,
  WarningTriangleIcon,
  EmptyState,
  SkeletonBlock,
  ConfirmDialog,
} from "./v2";

// Agents-tab grid — V2 rebuild 1:1 from ARTBOARD-SPEC boards 1a (card
// anatomy), 1b (search-match highlight), 1d (empty states), 1e (loading
// skeletons), 1f (variants + copy toast), 1g/1n (remove confirmation,
// desktop/mobile), 2g (credential + display title) and 1l (mobile cards).
//
// Presentation only (Brief Rule 6): every handler is the existing one —
// onAgentClick (card header + stat chip + View profile), onMakeAdmin /
// onRemoveAdmin (existing confirm flow), onRemoveFromAgency (existing
// removeAgentFromAgency call — the artboard's confirmation dialog is a
// presentation step IN FRONT of the unchanged handler; previously the
// action fired straight from the menu, flagged in FUNCTION-INVENTORY
// §12.2 and PARITY-FILL-AGENTS.md), copy = the existing
// navigator.clipboard call.
//
// Credential rule (supersedes board 2g's "Licensed" pill): the card
// credential is the FIFA Verified badge. Badge rule per Marin
// 2026-08-04: self-declaration carries the badge; only an explicit
// failed check ('failed_qr') hides it — the single gate is
// isFifaVerified (components/common/FifaVerifiedBadge.js).
// Its ABSENCE still renders nothing — "Unlicensed" never appears on a
// card (that split stays only in the admin toolbar filter, which keeps
// reading is_licensed; no rights change, display only).
//
// Follow-up wave (FOLLOWUP-PACK-BRIEF PACK B, 2026-07-23):
// - F-5 "Edit details": menu entry → the card's EXISTING DisplayTitleEditor
//   (autoOpen) — entry point to existing functionality only.
// - F-6 roles link: rendered in the 1d empty state ONLY when the page
//   passes an existing target handler (onRolesHelp → /faq, the role-aware
//   FAQ page); no handler → no link (no dead links).
// - F-7 "Show all N agents": presentation-level truncation (client-side
//   slice at COLLAPSED_COUNT per board 1a); expands to the full list;
//   collapse state resets when search/segment change.
// - F-8 + B4: own card now offers the self menu (View profile / Edit
//   details / Submit license when unlicensed / guarded self-demote) —
//   see AgentCardPopover; handlers stay the existing ones.

// Board 1a shows six cards above the ghost "Show all …" button.
const COLLAPSED_COUNT = 6;

// Board 1b: search-match highlight — the artboard's <mark> token
// (bg #FFF1B8, r3, padding 0 1px, color inherit) on every match.
const MARK_STYLE = {
  background: "#FFF1B8",
  borderRadius: 3,
  padding: "0 1px",
  color: "inherit",
};
const highlight = (text, query) => {
  const value = text || "";
  const q = (query || "").trim();
  if (!q) return value;
  const lower = value.toLowerCase();
  const ql = q.toLowerCase();
  const parts = [];
  let i = 0;
  let idx = lower.indexOf(ql);
  while (idx !== -1) {
    if (idx > i) parts.push(value.slice(i, idx));
    parts.push(
      <mark key={idx} style={MARK_STYLE} data-testid="agent-search-mark">
        {value.slice(idx, idx + q.length)}
      </mark>
    );
    i = idx + q.length;
    idx = lower.indexOf(ql, i);
  }
  if (parts.length === 0) return value;
  if (i < value.length) parts.push(value.slice(i));
  return parts;
};

// Board 1e: skeleton card anatomy (identity → stat → contacts), widths
// vary per card (ILLUSTRATIVE variance kept from the board).
const SKELETON_WIDTHS = [
  { name: "60%", sub: "38%", c1: "75%", c2: "50%" },
  { name: "52%", sub: "34%", c1: "68%", c2: "45%" },
  { name: "64%", sub: "40%", c1: "72%", c2: "48%" },
];
const SkeletonCard = ({ widths }) => (
  <Card gap={13} data-testid="agent-skeleton-card">
    <div className="flex gap-3 items-center">
      <SkeletonBlock circle height={44} />
      <div className="flex-1 flex flex-col gap-[7px]">
        <SkeletonBlock width={widths.name} height={13} />
        <SkeletonBlock width={widths.sub} height={10} tone="light" />
      </div>
    </div>
    <SkeletonBlock height={38} radius={10} tone="chip" />
    <div className="flex flex-col gap-[7px]">
      <SkeletonBlock width={widths.c1} height={10} tone="light" />
      <SkeletonBlock width={widths.c2} height={10} tone="light" />
    </div>
  </Card>
);

const AgentsGrid = ({
  agents,
  agentsLoading,
  agentsError,
  showPopover,
  popoverAgent,
  transferringAgentId,
  onAgentMenuClick,
  onClosePopover,
  onMakeAdmin,
  onRemoveAdmin,
  onRemoveFromAgency,
  onAgentClick,
  onAgentUpdated,
  isCurrentUserAdmin = false,
  currentUserAccountId = null,
  isMobile = false,
  searchQuery = "",
  segment = "all",
  totalCount = 0,
  onInviteAgent,
  onViewAllAgents,
  adminCount = 0,
  onSubmitLicense,
  onRolesHelp,
}) => {
  // Board 1f copy toast — transient presentation feedback after the
  // existing clipboard call; keyed per card so it shows under the copied
  // row's card only.
  const [copiedKey, setCopiedKey] = useState(null);
  const copyTimer = useRef(null);
  useEffect(() => () => clearTimeout(copyTimer.current), []);
  const handleCopy = (agent) => (value) => {
    if (navigator.clipboard) navigator.clipboard.writeText(value);
    setCopiedKey(agent.account_id);
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopiedKey(null), 2000);
  };

  // Boards 1g/1n remove confirmation — presentation step in front of the
  // EXISTING onRemoveFromAgency handler (wiring unchanged).
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const confirmRemove = async () => {
    if (!removeTarget || removing) return;
    setRemoving(true);
    try {
      await onRemoveFromAgency(removeTarget);
      setRemoveTarget(null);
    } finally {
      setRemoving(false);
    }
  };
  const removeFirstName = (removeTarget?.name || "").trim().split(/\s+/)[0];
  const removeManaging = removeTarget?.active_player_slots ?? 0;

  // F-5 — "Edit details" menu entry opens the target card's existing
  // display-title editor (the only existing member-detail edit surface).
  const [editTargetId, setEditTargetId] = useState(null);

  // F-7 — presentation-level truncation (board 1a ghost button). Client
  // slice only: the full roster is already loaded (no pagination exists).
  // Collapse state resets whenever the visible set changes meaningfully.
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    setShowAll(false);
  }, [searchQuery, segment]);

  if (agentsLoading) {
    return (
      <div className="px-6">
        <CardGrid>
          {SKELETON_WIDTHS.map((w, i) => (
            <SkeletonCard key={i} widths={w} />
          ))}
        </CardGrid>
      </div>
    );
  }

  if (agentsError) {
    return (
      <div className="px-6 pt-[18px]">
        <div className="text-center py-12 text-[13.5px] text-[#B91C1C] bg-white border border-[#E3E9F1] rounded-xl">
          {agentsError}
        </div>
      </div>
    );
  }

  if (!agents || agents.length === 0) {
    // Board 1d — "No agents yet": the roster itself is empty.
    // F-6: the "How roles work on TransferPitch" link renders only when
    // the page wires an existing target (role-aware /faq) — otherwise
    // nothing (no dead links).
    if (totalCount === 0) {
      return (
        <div className="px-6 pt-[18px]">
          <EmptyState
            icon={<UsersPlusIcon />}
            title="Build your agent roster"
            body="Invite your first agent to manage players and respond to club requests together."
            cta={
              onInviteAgent && (
                <Button icon={<PlusIcon />} onClick={onInviteAgent}>
                  Invite agent
                </Button>
              )
            }
            link={
              onRolesHelp && (
                <button
                  type="button"
                  onClick={onRolesHelp}
                  className="text-[13px] font-medium text-[#1668D9] bg-transparent border-0 cursor-pointer p-0"
                >
                  How roles work on TransferPitch
                </button>
              )
            }
          />
        </div>
      );
    }
    // Board 1d — "All agents are licensed": unlicensed filter, no matches.
    if (segment === "unlicensed" && !searchQuery.trim()) {
      return (
        <div className="px-6 pt-[18px]">
          <EmptyState
            small
            iconTone="success"
            icon={<CheckIcon size={20} strokeWidth={2.2} />}
            title="All agents are licensed"
            body="Nothing needs your attention here."
            link={
              onViewAllAgents && (
                <button
                  type="button"
                  onClick={onViewAllAgents}
                  className="text-[13px] font-semibold text-[#1668D9] bg-transparent border-0 cursor-pointer p-0"
                >
                  View all agents
                </button>
              )
            }
          />
        </div>
      );
    }
    // Other empty filter results (search handles its own live "0 agents
    // match" meta row in the toolbar).
    if (searchQuery.trim()) return null;
    return (
      <div className="px-6 pt-[18px]">
        <div className="text-center py-12 text-[13.5px] text-[#64748B] bg-white border border-[#E3E9F1] rounded-xl">
          No agents found
        </div>
      </div>
    );
  }

  // F-7 slice — presentation only; `agents` is the already-filtered list.
  const visibleCards = showAll ? agents : agents.slice(0, COLLAPSED_COUNT);
  const truncated = !showAll && agents.length > COLLAPSED_COUNT;

  return (
    <div className="px-6">
      <CardGrid>
        {visibleCards.map((agent) => {
          // Source of truth is the backend `is_admin` flag (real
          // AgencyAdmin group membership); legacy position-text fallback
          // for cached/older responses (FUNCTION-INVENTORY §9).
          const agentIsAdmin =
            agent.is_admin === true || agent?.position === "Agency Admin";
          const isLicensed =
            agent.is_licensed === true ||
            (agent.is_licensed === undefined && Boolean(agent.license_number));
          // Badge rule (Marin 2026-08-04): isFifaVerified — self-declared
          // licence OR fifa_verified, hidden only on failed_qr. isLicensed
          // above keeps driving the internal affordances (Submit license,
          // display-title derivation).
          const fifaVerified = isFifaVerified(agent);
          // F-9 (wave 2): dual-role lawyer badge from the serializer's
          // is_lawyer flag (BE PR #170) — strict boolean, absent field
          // (older cached rows) renders nothing.
          const isLawyer = agent.is_lawyer === true;
          const managing = agent?.active_player_slots ?? 0;
          // Own card gets the SELF menu (F-8/B4: Edit details, Submit
          // license while unlicensed, guarded self-demote) instead of the
          // admin-action menu.
          const isOwnCard =
            currentUserAccountId != null &&
            String(agent.account_id) === String(currentUserAccountId);
          const menuOpen =
            showPopover && popoverAgent?.account_id === agent.account_id;
          return (
            <Card
              key={agent.account_id}
              className="relative"
              style={menuOpen ? { zIndex: 5 } : undefined}
            >
              {/* header: avatar → name / display title / pills → overflow */}
              <CardHeader
                avatar={
                  <PersonAvatar
                    src={agent?.profile_picture_url || null}
                    name={agent.name}
                    seed={agent.account_id}
                    size={44}
                    onClick={() => onAgentClick(agent?.account_id)}
                    style={{ cursor: "pointer" }}
                  />
                }
                action={
                  isCurrentUserAdmin ? (
                    <OverflowButton
                      variant={isMobile ? "mobileOverflow" : "default"}
                      open={menuOpen}
                      onClick={() => onAgentMenuClick(agent)}
                      aria-label="Agent actions"
                    />
                  ) : undefined
                }
                identityGap={3}
              >
                <span
                  onClick={() => onAgentClick(agent?.account_id)}
                  className="cursor-pointer min-w-0 flex flex-col gap-[3px]"
                >
                  <CardName mobile={isMobile}>
                    {highlight(agent.name, searchQuery)}
                  </CardName>
                </span>
                <DisplayTitleEditor
                  agent={agent}
                  licensed={isLicensed}
                  editable={isOwnCard || isCurrentUserAdmin}
                  adminAccountId={
                    !isOwnCard && isCurrentUserAdmin ? agent.account_id : undefined
                  }
                  onSaved={onAgentUpdated}
                  autoOpen={editTargetId === agent.account_id}
                  onAutoOpenConsumed={() => setEditTargetId(null)}
                />
                {(agentIsAdmin || isLawyer || fifaVerified) && (
                  <div className="flex items-center gap-1.5 flex-wrap mt-[3px]">
                    {/* Pill order (boards 1f/1a, binding): Admin →
                        Lawyer → license signal (now the D-A badge). */}
                    {agentIsAdmin && <AdminPill>Admin</AdminPill>}
                    {isLawyer && <LawyerPill>Lawyer</LawyerPill>}
                    {fifaVerified && <FifaVerifiedBadge size="md" />}
                  </div>
                )}
              </CardHeader>

              {/* desktop popover anchored to the card (board 1g; own card
                  = self menu per 1k demo 3 + B4) */}
              {!isMobile && menuOpen && (
                <AgentCardPopover
                  isOpen
                  onClose={onClosePopover}
                  agent={popoverAgent}
                  onViewProfile={(a) => onAgentClick(a?.account_id)}
                  onEditDetails={(a) => setEditTargetId(a?.account_id)}
                  onMakeAdmin={onMakeAdmin}
                  onRemoveAdmin={onRemoveAdmin}
                  onRemoveFromAgency={setRemoveTarget}
                  onSubmitLicense={
                    isOwnCard && !isLicensed ? onSubmitLicense : undefined
                  }
                  isLoading={transferringAgentId === agent.account_id}
                  isAgentAnAdmin={agentIsAdmin}
                  isOwnCard={isOwnCard}
                  isLastAdmin={isOwnCard && agentIsAdmin && adminCount <= 1}
                />
              )}

              {/* managing-players stat chip → agent profile (existing nav) */}
              <StatChip
                label="Managing players"
                value={<span data-no-translate>{managing}</span>}
                muted={!(managing > 0)}
                interactive
                role="button"
                tabIndex={0}
                onClick={() => onAgentClick(agent?.account_id)}
                padding={isMobile ? "10px 12px" : "9px 12px"}
              />

              {/* contact rows — only when data exists (no N/A rows) */}
              <div className="flex flex-col gap-1.5">
                <ContactRow
                  kind="email"
                  value={agent.email}
                  copyTitle="Copy email"
                  onCopy={handleCopy(agent)}
                  mobile={isMobile}
                  display={highlight(agent.email, searchQuery)}
                />
                <ContactRow
                  kind="phone"
                  value={agent.phone_number}
                  copyTitle="Copy phone"
                  onCopy={handleCopy(agent)}
                  mobile={isMobile}
                />
                {copiedKey === agent.account_id && (
                  <CopyToast>Copied to clipboard</CopyToast>
                )}
              </div>
            </Card>
          );
        })}
      </CardGrid>

      {/* F-7 — ghost "Show all N agents" (board 1a): centered, mt 20. */}
      {truncated && (
        <div className="flex justify-center" style={{ marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setShowAll(true)}>
            Show all <span data-no-translate>{agents.length} </span>
            {agents.length === 1 ? "agent" : "agents"}
          </Button>
        </div>
      )}

      {/* mobile action sheet (board 1m; own card = self menu) */}
      {isMobile &&
        showPopover &&
        popoverAgent &&
        (() => {
          const popIsOwn =
            currentUserAccountId != null &&
            String(popoverAgent.account_id) === String(currentUserAccountId);
          const popIsAdmin =
            popoverAgent.is_admin === true ||
            popoverAgent?.position === "Agency Admin";
          const popLicensed =
            popoverAgent.is_licensed === true ||
            (popoverAgent.is_licensed === undefined &&
              Boolean(popoverAgent.license_number));
          return (
            <AgentCardPopover
              isOpen
              mobile
              onClose={onClosePopover}
              agent={popoverAgent}
              onViewProfile={(a) => onAgentClick(a?.account_id)}
              onEditDetails={(a) => setEditTargetId(a?.account_id)}
              onMakeAdmin={onMakeAdmin}
              onRemoveAdmin={onRemoveAdmin}
              onRemoveFromAgency={setRemoveTarget}
              onSubmitLicense={
                popIsOwn && !popLicensed ? onSubmitLicense : undefined
              }
              isLoading={transferringAgentId === popoverAgent.account_id}
              isAgentAnAdmin={popIsAdmin}
              isOwnCard={popIsOwn}
              isLastAdmin={popIsOwn && popIsAdmin && adminCount <= 1}
              licensed={popLicensed}
              verified={isFifaVerified(popoverAgent)}
              managingCount={popoverAgent?.active_player_slots ?? 0}
            />
          );
        })()}

      {/* remove confirmation (boards 1g desktop / 1n mobile) */}
      <ConfirmDialog
        open={!!removeTarget}
        mobile={isMobile}
        tone="danger"
        icon={<WarningTriangleIcon size={18} stroke="#DC2626" />}
        title={
          <>
            Remove <span data-no-translate>{removeTarget?.name}</span> from the
            agency?
          </>
        }
        body={
          removeManaging > 0 ? (
            <>
              <span data-no-translate>{removeFirstName}</span> immediately
              loses access to your agency, including the{" "}
              <b className="text-[#0F172A]" data-no-translate>
                {removeManaging} {removeManaging === 1 ? "player" : "players"}
              </b>{" "}
              they manage, which will need reassigning. This can’t be undone.
            </>
          ) : (
            <>
              <span data-no-translate>{removeFirstName}</span> immediately
              loses access to your agency. This can’t be undone.
            </>
          )
        }
        confirmLabel="Remove agent"
        cancelLabel="Cancel"
        loading={removing}
        onClose={() => (removing ? null : setRemoveTarget(null))}
        onConfirm={confirmRemove}
      />
    </div>
  );
};

export default AgentsGrid;
